import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { resolveImageUrl } from "../../lib/format";

const empty = { name: "", slug: "", description: "", image_url: "", banner_url: "", order: 0 };

export default function AdminCategories() {
    const [cats, setCats] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [uploading, setUploading] = useState("");
    const [dragIndex, setDragIndex] = useState(null);

    const load = async () => {
        const { data } = await api.get("/admin/categories");
        setCats(data || []);
    };
    useEffect(() => { load(); }, []);

    const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

    const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
    const openEdit = (c) => { setEditing(c); setForm({ ...c }); setOpen(true); };

    const handleImage = async (file, field) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setUploading(field);
        try {
            const { data } = await api.post("/admin/products/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((f) => ({ ...f, [field]: data.url }));
            toast.success("Imagen subida.");
        } catch (err) { toast.error(apiErrorMessage(err)); }
        finally { setUploading(""); }
    };

    const onDrop = async (index) => {
        if (dragIndex === null || dragIndex === index) { setDragIndex(null); return; }
        const next = [...cats];
        const [moved] = next.splice(dragIndex, 1);
        next.splice(index, 0, moved);
        setCats(next);
        setDragIndex(null);
        try {
            await api.post("/admin/categories/reorder", { ids: next.map((c) => c.id) });
        } catch (err) { toast.error(apiErrorMessage(err)); await load(); }
    };

    const submit = async (e) => {
        e.preventDefault();
        const payload = { ...form, order: Number(form.order) || 0 };
        delete payload.product_count;
        delete payload.id; delete payload.created_at; delete payload.updated_at;
        try {
            if (editing) {
                await api.patch(`/admin/categories/${editing.id}`, payload);
                toast.success("Categoría actualizada.");
            } else {
                await api.post("/admin/categories", payload);
                toast.success("Categoría creada.");
            }
            setOpen(false); setEditing(null); setForm(empty);
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const remove = async (c) => {
        if (!window.confirm(`¿Eliminar la categoría "${c.name}"? Se quitará de los plugins que la tengan.`)) return;
        try {
            await api.delete(`/admin/categories/${c.id}`);
            toast.success("Categoría eliminada.");
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    return (
        <DashboardLayout role="admin">
            <div className="flex items-start justify-between gap-4 mb-8">
                <div data-testid="admin-categories-page">
                    <div className="label-mono mb-3">/ Taxonomía</div>
                    <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Categorías</h1>
                    <p className="text-sm text-avx-muted mt-2 max-w-2xl">
                        Crea las categorías de plugins (ej: Ecualizadores, Compresores, Reverbs). Luego asígnalas a cada
                        plugin desde Products. Un plugin puede tener varias. Se usan como filtro y etiqueta en el catálogo.
                    </p>
                </div>
                <button onClick={openCreate} className="btn-primary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm shrink-0" data-testid="create-category-btn">+ Nueva categoría</button>
            </div>

            {cats.length === 0 ? (
                <div className="panel p-10 text-center text-avx-muted" data-testid="no-categories">Aún no hay categorías. Crea la primera.</div>
            ) : (
                <>
                <p className="text-[11px] font-mono text-avx-muted mb-3">Arrastra las tarjetas para cambiar el orden. Se guarda automáticamente.</p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cats.map((c, i) => (
                        <div
                            key={c.id}
                            draggable
                            onDragStart={() => setDragIndex(i)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => onDrop(i)}
                            onDragEnd={() => setDragIndex(null)}
                            className={`panel-elevated p-5 flex flex-col cursor-grab active:cursor-grabbing transition-opacity ${dragIndex === i ? "opacity-40" : ""}`}
                            data-testid={`category-${c.slug}`}
                        >
                            <div className="flex items-start gap-3 mb-3">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-4 h-4 text-avx-muted mt-1 shrink-0"><circle cx="9" cy="6" r="1"/><circle cx="9" cy="12" r="1"/><circle cx="9" cy="18" r="1"/><circle cx="15" cy="6" r="1"/><circle cx="15" cy="12" r="1"/><circle cx="15" cy="18" r="1"/></svg>
                                {c.image_url ? (
                                    <img src={resolveImageUrl(c.image_url)} alt={c.name} className="w-12 h-12 rounded-sm object-cover border border-avx-line" />
                                ) : (
                                    <div className="w-12 h-12 rounded-sm bg-avx-bg border border-avx-line flex items-center justify-center text-avx-muted font-mono text-lg">{c.name.charAt(0)}</div>
                                )}
                                <div className="min-w-0">
                                    <h3 className="font-heading font-bold text-base leading-tight">{c.name}</h3>
                                    <div className="label-mono text-[10px]">{c.slug} · {c.product_count} plugins</div>
                                </div>
                            </div>
                            {c.description && <p className="text-xs text-avx-muted line-clamp-2 mb-3">{c.description}</p>}
                            <div className="flex gap-2 mt-auto">
                                <button onClick={() => openEdit(c)} className="flex-1 btn-secondary py-2 text-xs font-mono uppercase tracking-[0.18em] rounded-sm" data-testid={`edit-category-${c.slug}`}>Editar</button>
                                <button onClick={() => remove(c)} className="px-3 py-2 text-xs font-mono uppercase tracking-[0.18em] rounded-sm border border-avx-danger/30 text-avx-danger hover:bg-avx-danger/10" data-testid={`delete-category-${c.slug}`}>Eliminar</button>
                            </div>
                        </div>
                    ))}
                </div>
                </>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-avx-surface border-avx-line max-w-lg">
                    <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar categoría" : "Nueva categoría"}</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <Label className="label-mono">Nombre</Label>
                            <Input className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.name} required data-testid="category-name"
                                onChange={(e) => { const name = e.target.value; setForm((f) => ({ ...f, name, slug: editing ? f.slug : slugify(name) })); }} />
                        </div>
                        <div>
                            <Label className="label-mono">Slug</Label>
                            <Input className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.slug} required data-testid="category-slug"
                                onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} />
                        </div>
                        <div>
                            <Label className="label-mono">Descripción (opcional)</Label>
                            <Textarea rows={2} className="mt-2 bg-avx-bg border-avx-line" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-2 gap-3 items-start">
                            <div>
                                <Label className="label-mono">Ícono (card)</Label>
                                <Input type="file" accept="image/*" className="mt-2 bg-avx-bg border-avx-line text-xs" onChange={(e) => handleImage(e.target.files?.[0], "image_url")} data-testid="category-image-upload" />
                                {uploading === "image_url" && <div className="text-xs text-avx-blue font-mono mt-1">Subiendo…</div>}
                                {form.image_url && <img src={resolveImageUrl(form.image_url)} alt="icono" className="mt-2 w-14 h-14 rounded-sm object-cover border border-avx-line" />}
                            </div>
                            <div>
                                <Label className="label-mono">Banner social (opcional)</Label>
                                <Input type="file" accept="image/*" className="mt-2 bg-avx-bg border-avx-line text-xs" onChange={(e) => handleImage(e.target.files?.[0], "banner_url")} data-testid="category-banner-upload" />
                                {uploading === "banner_url" && <div className="text-xs text-avx-blue font-mono mt-1">Subiendo…</div>}
                                {form.banner_url && <img src={resolveImageUrl(form.banner_url)} alt="banner" className="mt-2 w-full h-14 rounded-sm object-cover border border-avx-line" />}
                                <p className="text-[10px] font-mono text-avx-muted mt-1">Se usa como banner de la página y al compartir el link.</p>
                            </div>
                        </div>
                        <button type="submit" className="w-full btn-primary py-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="save-category-btn">
                            {editing ? "Guardar cambios" : "Crear categoría"}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
